import React from 'react';
import { Heart, Star, MessageCircle, PenTool } from 'lucide-react';
export function TestimonialVariant() {
  return <div className="min-h-screen w-full bg-[#FFFBF0] font-sans text-slate-800 overflow-x-hidden" data-id="element-324">
      {/* Hand-drawn style header */}
      <header className="py-8 px-4 text-center relative" data-id="element-325">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300" data-id="element-326"></div>
        <h1 className="font-['Caveat'] text-5xl md:text-6xl font-bold text-slate-800 mb-2 transform -rotate-1" data-id="element-327">
          Dog Parent Confessions
        </h1>
        <p className="font-['Permanent_Marker'] text-slate-500 rotate-1" data-id="element-328">
          Real stories. Real struggles. Real relief.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-4 pb-24" data-id="element-329">
        {/* Hero Polaroid */}
        <div className="bg-white p-4 pb-12 shadow-lg rotate-1 max-w-2xl mx-auto mb-16 transform transition-transform hover:rotate-0 duration-300 relative" data-id="element-330">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-32 h-8 bg-yellow-200/80 shadow-sm transform -rotate-2" data-id="element-331"></div>
          <img src="https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?q=80&w=2069&auto=format&fit=crop" alt="Sad dog owner" className="w-full h-auto aspect-video object-cover grayscale contrast-125 mb-4" data-id="element-332" />
          <h2 className="font-['Caveat'] text-3xl text-center leading-tight" data-id="element-333">
            "I felt like I was hurting the thing I love the most."
          </h2>
        </div>

        {/* The Problem - Sticky Note Style */}
        <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-334">
          <div className="bg-[#FFFCBB] p-6 shadow-md transform -rotate-1" data-id="element-335">
            <h3 className="font-bold text-lg mb-2 font-['Permanent_Marker'] text-slate-700" data-id="element-336">
              The "Walk-Time Cough"
            </h3>
            <p className="font-['Caveat'] text-2xl leading-relaxed" data-id="element-337">
              It starts small. A tiny hack-hack. A weird "honking" sound. You
              tell yourself "He's just excited." But then one day... the leash
              snaps tight and they cough like they're choking.
            </p>
          </div>
          <div className="bg-[#FFD1DC] p-6 shadow-md transform rotate-2" data-id="element-338">
            <h3 className="font-bold text-lg mb-2 font-['Permanent_Marker'] text-slate-700" data-id="element-339">
              The Guilt Loop
            </h3>
            <p className="font-['Caveat'] text-2xl leading-relaxed" data-id="element-340">
              Your dog pulls → you tighten control → they cough → you feel
              guilty → you dread walks. It's the worst feeling in the world.
            </p>
          </div>
        </div>

        {/* Narrative Section */}
        <div className="prose prose-lg prose-slate max-w-none font-medium bg-white p-8 md:p-12 rounded-2xl shadow-sm border-2 border-dashed border-slate-300 mb-16" data-id="element-341">
          <p data-id="element-342">
            If you're the kind of person who treats your dog like family, that
            coughing sound hits different. It attacks your identity.
          </p>
          <p data-id="element-343">
            <strong data-id="element-344">The uncomfortable truth:</strong> A dog's neck is not a
            handle. It's delicate. It has the windpipe, nerves, and blood
            vessels.
          </p>
          <p data-id="element-345">
            Yet we use collars. Or "no-pull" harnesses that still ride up into
            the throat.
          </p>
          <div className="flex items-center gap-4 not-prose bg-slate-50 p-4 rounded-lg my-6" data-id="element-346">
            <div className="bg-red-100 p-2 rounded-full text-red-500" data-id="element-347">
              <Heart fill="currentColor" data-id="element-348" />
            </div>
            <p className="text-sm italic text-slate-600 m-0" data-id="element-349">
              "I shouldn't be dreading walking my own dog." — You (probably)
            </p>
          </div>
        </div>

        {/* The Solution - Scrapbook Collage */}
        <div className="relative py-12 mb-16" data-id="element-350">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cork-board.png')] opacity-10 z-0" data-id="element-351"></div>

          <h2 className="relative z-10 text-center font-['Permanent_Marker'] text-3xl mb-12" data-id="element-352">
            The "ChestFlow" Difference
          </h2>

          <div className="grid md:grid-cols-3 gap-6 relative z-10" data-id="element-353">
            {[{
            title: 'No Choking',
            desc: 'Pressure stays off the throat 100%',
            color: 'bg-green-100'
          }, {
            title: 'No Escapes',
            desc: "Secure fit they can't back out of",
            color: 'bg-blue-100'
          }, {
            title: 'No Rubbing',
            desc: 'Soft edges, no armpit chafing',
            color: 'bg-purple-100'
          }].map((item, i) => <div key={i} className={`${item.color} p-6 shadow-md rounded-sm transform ${i % 2 === 0 ? 'rotate-1' : '-rotate-1'}`} data-id="element-354">
                <div className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center mb-3 font-bold" data-id="element-355">
                  {i + 1}
                </div>
                <h3 className="font-bold text-lg mb-2" data-id="element-356">{item.title}</h3>
                <p className="font-['Caveat'] text-xl" data-id="element-357">{item.desc}</p>
              </div>)}
          </div>
        </div>

        {/* Community Proof */}
        <div className="mb-16" data-id="element-358">
          <h2 className="text-center font-['Caveat'] text-4xl mb-8" data-id="element-359">
            From the Community ❤️
          </h2>
          <div className="space-y-6" data-id="element-360">
            {[{
            name: 'Sarah & Max',
            text: 'The coughing stopped and I felt like I could finally breathe too.',
            stars: 5
          }, {
            name: 'Mike & Luna',
            text: "I've tried 4 harnesses. This is the only one I trust.",
            stars: 5
          }, {
            name: 'Jen & Cooper',
            text: 'Walks feel like bonding again instead of a battle.',
            stars: 5
          }].map((review, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm flex gap-4 items-start" data-id="element-361">
                <div className="bg-slate-100 w-12 h-12 rounded-full flex items-center justify-center shrink-0" data-id="element-362">
                  <span className="font-['Permanent_Marker'] text-slate-400" data-id="element-363">
                    {review.name[0]}
                  </span>
                </div>
                <div data-id="element-364">
                  <div className="flex text-yellow-400 mb-1" data-id="element-365">
                    {[...Array(review.stars)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-366" />)}
                  </div>
                  <p className="text-lg font-['Caveat'] text-slate-800 text-xl mb-2" data-id="element-367">
                    "{review.text}"
                  </p>
                  <p className="text-xs font-bold uppercase text-slate-400" data-id="element-368">
                    {review.name}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* CTA - Handwritten Letter */}
        <div className="bg-white p-8 md:p-16 shadow-xl max-w-2xl mx-auto relative overflow-hidden" data-id="element-369">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-30" data-id="element-370"></div>

          <div className="relative z-10 text-center" data-id="element-371">
            <h3 className="font-['Caveat'] text-4xl mb-6" data-id="element-372">One last thing...</h3>
            <p className="font-medium text-slate-600 mb-8 leading-relaxed" data-id="element-373">
              If you're still using a collar, you're not a bad dog parent.
              You're just doing what everyone was taught. But once you see the
              neck issue, you can't unsee it.
            </p>
            <p className="font-['Permanent_Marker'] text-xl text-slate-800 mb-8" data-id="element-374">
              It lets good dog parents feel good again.
            </p>

            <button className="bg-slate-900 text-white font-['Permanent_Marker'] text-xl px-8 py-4 rounded-sm transform -rotate-1 hover:rotate-0 hover:scale-105 transition-all shadow-lg" data-id="element-375">
              Check Availability →
            </button>
          </div>
        </div>
      </main>
    </div>;
}